import {
	ReactElement,
	useMemo,
	useState
} from "react";
import styles from "../../Users/UserAndUserGroupCollection/UserAndUserGroupCollection.module.less";
import {
	SearchInput,
	TransparentButtonList
} from "client/Components";
import {
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import {
	UserGroupOverviewModel
} from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	UserGroupCollection,
	UserGroupCollectionMode
} from "client/Features/Users/UserAndUserGroupCollection/UserGroupCollection";
import { unique } from "client_legacy/ndaw/arrayHelpers";
import { SelectUserGroupDialog } from "./SelectUserGroupDialog";
import { IValueInputAttachable } from "client/Forms/attachable";

export type UserGroupConfigurationProps = {
	value: readonly number[];
	onChange?: (newValue: readonly number[]) => void;
	data: readonly UserGroupOverviewModel[];
	trackingId: string;
};

export const UserGroupConfiguration = (props: UserGroupConfigurationProps): ReactElement => {
	const {
		value,
		onChange,
		data,
		trackingId
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

	const mode = useMemo(
		(): UserGroupCollectionMode => {
			if (isNil(onChange)) {
				return {
					kind: UserAndUserGroupKind.Read
				};
			}
			return {
				kind: UserAndUserGroupKind.Edit,
				onChange
			};
		},
		[onChange]
	);

	const addHandler = (collection: readonly UserGroupOverviewModel[]): void => {
		if (mode.kind === UserAndUserGroupKind.Read) {
			throw new Error("Add is not supported in read mode");
		}

		const newValue: readonly number[] = unique([
			...value,
			...collection.map(x => x.id)
		]);

		mode.onChange(newValue);
		setIsDialogOpen(false);
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{isNotNil(onChange) && (
				<div className={styles.buttonList}>
					<TransparentButtonList>
						<TransparentButtonList.Item
							trackingId="add-users-or-groups"
							icon="plus"
							text={localize("_Add")}
							onClick={() => setIsDialogOpen(true)}
						/>
					</TransparentButtonList>
				</div>
			)}
			<div className={styles.searchAndTabSelection}>
				<div className={styles.search}>
					<SearchInput
						trackingId="search"
						onChange={setSearchTerm}
					/>
				</div>
			</div>
			<div className={styles.tableContainer}>
				<UserGroupCollection
					allData={data}
					value={value}
					searchTerm={searchTerm}
					mode={mode}
					height="stretch"
				/>
			</div>

			{(isDialogOpen && mode.kind === UserAndUserGroupKind.Edit) && (
				<SelectUserGroupDialog
					data={data}
					onSelected={addHandler}
					onClose={() => setIsDialogOpen(false)}
				/>
			)}
		</div>
	);
};
UserGroupConfiguration.displayName = "UserGroupConfiguration";

export function attachToUserGroupConfiguration(
	attachable: IValueInputAttachable<readonly number[] | null>,
	mode: UserAndUserGroupKind,
): Pick<UserGroupConfigurationProps, "value" | "onChange" | "trackingId"> {

	let onChange: ((newValue: readonly number[]) => void) | undefined;
	if (mode === UserAndUserGroupKind.Edit) {
		onChange = (newValue: readonly number[]): void => {
			attachable.reducer
				.setValue(newValue);

			attachable.validation
				.triggerInputFinished();
		};
	}

	return {
		value: attachable.value ?? EMPTY_ARRAY,
		onChange,
		trackingId: attachable.reducer
			.path
	};
}
